﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// A page that will load when clicking on the confirm link in the "Confirm email address" mail.

// Can be tested in jRåd.
// Register an account through self service and then login to it.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import Grid from "~base/Components/Layout/Grid";
import { useIntl } from "~base/Internationalization/IntlContext";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import { reducer } from "./Store";
import { useSettings } from "~base/Settings/SettingsContext";
import ApplicationPage from "~base/Pages/Shared/ApplicationPage";
import { useSearchParams } from "react-router-dom";
import { RouteLink } from "~base/Components/Primitives/Link";
import { getErrorMessage } from "~base/Services/API";
import { useEffect } from "react";
import { useNavigateService } from "~base/Services/NavigateService";
import { useAccountPostConfirmEmail } from "~base/Services/API/Account/PostConfirmEmail";
import TextBlock from "~base/Components/Primitives/TextBlock";
import TitleButton from "~base/Components/Primitives/TitleButton";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import classnames from "classnames";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type ConfirmEmailProps = unknown;

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const ConfirmEmailPage: React.FunctionComponent<ConfirmEmailProps> = (
    props: ConfirmEmailProps
) => {
    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("ConfirmEmailPage"),
        reducer,
        { errorMessage: null, loading: true, isVerified: false }
    );

    const settings = useSettings();
    const navigate = useNavigateService();
    const serverVirtualPath = settings.valueAsString("serverVirtualPath");
    const tenantName = settings.valueAsString("detectedTenantName");

    const [searchParams] = useSearchParams();
    const usernameFromQs = searchParams.get("username");
    const tokenFromQs = searchParams.get("token");

    const jssSpacingStyles = useJssSpacingStyles();

    const formBottomMarginClass = getSpacingClasses(jssSpacingStyles, null, {
        size: "XL",
        dir: "Bottom",
    });
    const formHorizontalMarginClass = getSpacingClasses(
        jssSpacingStyles,
        null,
        { size: "HalfMedium", dir: "Horizontal" }
    );

    const intl = useIntl();

    const loginUri = `${serverVirtualPath}Account/Login?username=${encodeURIComponent(usernameFromQs)}`;

    const [loadingAccountConfirmEmailApi, __] = useAccountPostConfirmEmail({
        call: state.loading && !state.errorMessage && !state.isVerified,
        data: {
            Username: usernameFromQs,
            Code: tokenFromQs,
            Tenant: tenantName,
        },
        callback: async (status, response, _, requestData) => {
            if (status == "Success") {
                dispatch({ type: "setVerified" });
            } else {
                dispatch({
                    type: "setError",
                    errorMessage: await getErrorMessage(intl, response),
                });
            }
        },
    });

    useEffect(() => {
        if (state.navigateUri) {
            navigate.current(state.navigateUri);
        }
    }, [navigate, state.navigateUri]);

    const isLoading = loadingAccountConfirmEmailApi || state.loading;

    return (
        <ApplicationPage isLoading={isLoading}>
            <Grid templateColumns="">
                <Rubrik textCode="VisiFinance.UI.ConfirmAccount.Title" />

                {state.isVerified ? (
                    <>
                        <TextBlock
                            textCode="VisiFinance.UI.ConfirmAccount.SuccessfullyConfirmed"
                            cssClass={classnames(
                                formBottomMarginClass,
                                formHorizontalMarginClass
                            )}
                        />
                        <TitleButton
                            testIdentifier={"ConfirmEmailGoToLoginButton"}
                            styleType={"DefaultPadded"}
                            onClick={() =>
                                dispatch({ type: "navigate", uri: loginUri })
                            }
                        >
                            {intl.formatText(
                                "VisiFinance.UI.ConfirmAccount.GoToLogin"
                            )}
                        </TitleButton>
                    </>
                ) : (
                    <>
                        {state.errorMessage && (
                            <TextBlock
                                styleType="Paragraph"
                                text={state.errorMessage}
                                styleSubType="Validation"
                            />
                        )}

                        <RouteLink
                            testIdentifier={
                                "ConfirmEmailChangeConfirmationMethod"
                            }
                            textCode="VisiFinance.UI.ConfirmAccount.ChangeConfirmationMethod"
                            to={`/Account/Confirm?username=${encodeURIComponent(usernameFromQs)}`}
                        />
                    </>
                )}
            </Grid>
        </ApplicationPage>
    );
};
